import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import CmdK from "../components/CmdK";
import NotificationDrawer from "../components/NotificationDrawer";
import GeofenceLockout from "../components/GeofenceLockout";
import SubscriptionBanner from "../components/SubscriptionBanner";
import { useAuth } from "../contexts/AuthContext";
import { useRefresh } from "../contexts/RefreshContext";
import { useSupportWidget } from "../hooks/useSupportWidget";
import { logout as apiLogout } from "../api/authService";
import { getCurrentFcmToken, clearCurrentFcmToken } from "../hooks/useFcmToken";
import {
  getNotifications,
  markAllRead,
  markRead,
} from "../api/notificationService";
import { chatUnreadCount } from "../api/chatService";
import { checkGeofence } from "../api/geofenceService";
import { getPosition, isPermissionReason } from "../utils/geolocation";
import { showApiError } from "../utils/toast";

// Geofence poll cadence: relaxed while everything is fine, tightened once the
// lockout overlay is up so someone who has stepped back inside — or just
// granted the location permission — is let back in within seconds instead of
// waiting out a full minute staring at a blocking screen.
const FENCE_POLL_MS = 60_000;
const FENCE_POLL_BLOCKED_MS = 15_000;

export default function AppLayout() {
  const { user, role, logout, hasAddon, refreshMe } = useAuth();
  const navigate = useNavigate();
  const { refreshKey } = useRefresh();

  // Chat (addon-gated) — quick-access button + nav badge fed by one poll.
  const chatEnabled = (role === "admin" || role === "employee") && hasAddon("chat", "chat_extended");
  const chatPath = role === "admin" ? "/admin/chat" : "/me/chat";

  // Attendance (addon-gated) — top-bar status + quick check-in/out popover.
  // Same gating as chat: staff only (admins are tracked too), never customers.
  const attendanceEnabled = (role === "admin" || role === "employee") && hasAddon("attendance");
  const [chatUnread, setChatUnread] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdKOpen, setCmdKOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState({ notifications: { data: [] }, unread_count: 0 });

  // Geofence (addon-gated) — only fenced employees are watched. null = allowed;
  // { reason } = blocked (outside the fence or no location available).
  const geofenceActive = role === "employee" && !!user?.geofenced;
  const [fenceBlock, setFenceBlock] = useState(null);
  const [fenceChecking, setFenceChecking] = useState(false);
  // Seconds until the next automatic check, shown on the overlay. Only tracked
  // while blocked — see the poll effect below.
  const [secondsLeft, setSecondsLeft] = useState(null);
  const fenceInFlight = useRef(false);

  // Support Hub chat widget — admins and employees only (customers don't get it).
  useSupportWidget(role === "admin" || role === "employee");

  // Close the mobile sidebar on viewport resize back to desktop.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 720) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // FCM is bootstrapped at App-level (FcmBootstrap), so it works on every
  // route — including the login screen — and reacts to auth state changes.

  // Re-fetch notifications (and chat unread) when an FCM message arrives
  // (refreshKey bumps) and on a 60s background poll.
  useEffect(() => {
    fetchNotifications();
    fetchChatUnread();
    const id = setInterval(() => { fetchNotifications(); fetchChatUnread(); }, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const fetchNotifications = async () => {
    try {
      const data = await getNotifications();
      setNotifications(data);
    } catch (err) {
      // silent — background poll
    }
  };

  const fetchChatUnread = async () => {
    if (!chatEnabled) return;
    try {
      const res = await chatUnreadCount();
      setChatUnread(res?.data?.unread || 0);
    } catch {
      // silent — background poll
    }
  };

  // Periodic geofence re-check: read the device location and ask the server
  // whether it's inside an assigned fence. A denied/unavailable location is
  // fail-closed (blocked), matching the login gate. A transient network error
  // leaves the current state untouched.
  // Resolves when the check settles. Toggles `fenceChecking` so the overlay
  // shows a spinner for BOTH the manual button and the automatic 60s poll.
  //
  // `highAccuracy` is off for the background poll: a fence is a 100m+ radius,
  // so wifi/cell positioning decides in/out just as well, resolves indoors
  // where GPS times out, and doesn't wake the GPS every minute. The manual
  // "Check again" passes true, so the battery cost only lands when asked for.
  // `maxAgeMs` caps how stale a cached fix may be. The default suits the
  // relaxed poll, but the blocked poll passes its own (shorter) period —
  // otherwise a 15s cadence would keep re-submitting the same 90s-old
  // coordinates and someone who walked back inside would stay locked out.
  const runFenceCheck = useCallback(async (highAccuracy = false, maxAgeMs = 90000) => {
    // Collapse a burst of concurrent triggers (parallel 403s, a tick landing on
    // a manual retry) into one geolocation read and one round trip.
    if (fenceInFlight.current) return;
    fenceInFlight.current = true;
    setFenceChecking(true);
    try {
      const fix = await getPosition(
        highAccuracy
          ? { highAccuracy: true, timeout: 10000, maximumAge: 0 }
          : { highAccuracy: false, timeout: 12000, maximumAge: maxAgeMs },
      );

      if (!fix.ok) {
        // Only a real permission problem gets the "turn on location" screen.
        // A timeout or missing fix is a different situation with different
        // advice — telling someone who granted access to grant access is the
        // bug this split exists to fix.
        const reason = isPermissionReason(fix.reason) ? "location_required" : "location_unavailable";
        // Tell the server we could not prove our location. GeofenceGuard is
        // fail-closed on null coordinates, so for a fenced user this locks the
        // session token and EnsureInsideGeofence blocks the rest of the API —
        // otherwise denying the permission prompt silently bypasses the fence
        // and the overlay is pure decoration.
        try {
          const res = await checkGeofence(null, null);
          // ...but a user the fence does not apply to is allowed regardless of
          // coordinates, so don't strand them behind a permission they don't
          // actually need.
          setFenceBlock(res?.data?.inside ? null : { reason });
        } catch {
          // Couldn't reach the server — assume the worst, matching the login gate.
          setFenceBlock({ reason });
        }
        return;
      }

      try {
        const res = await checkGeofence(fix.coords.latitude, fix.coords.longitude);
        const data = res?.data || {};
        setFenceBlock(data.inside ? null : { reason: data.reason || "outside" });
      } catch {
        // transient network/server error — keep whatever we last showed
      }
    } finally {
      fenceInFlight.current = false;
      setFenceChecking(false);
    }
  }, []);

  // The overlay's retry button asks for a precise, fresh fix, then re-reads
  // /me so someone whose fence was removed while they were blocked can free
  // themselves without reloading.
  const retryFenceCheck = useCallback(async () => {
    // Check first: for a user who is no longer fenced the server answers
    // "allowed", which is what clears a lock still sitting on the token.
    await runFenceCheck(true);
    const fresh = await refreshMe({ force: true });
    // If the addon was switched off entirely, /geofence/check is itself
    // addon-gated and 403s with no code, so the check above cannot clear
    // anything — trust /me and drop the overlay.
    if (fresh && !fresh.geofenced) setFenceBlock(null);
  }, [refreshMe, runFenceCheck]);

  // Boolean rather than `fenceBlock` itself: every poll result builds a fresh
  // object, so depending on the object would restart the timer on each tick.
  const fenceBlocked = !!fenceBlock;

  // Admin removed the fence or switched the addon off (picked up by the
  // periodic /me refresh) — drop the overlay without a page reload.
  //
  // Keyed on geofenceActive ALONE, deliberately. The ticker below also depends
  // on `fenceBlocked`, so folding this clear into it would re-run the moment
  // the listener raises a block on a session whose cached flag is still stale
  // — wiping the overlay a frame after it appeared.
  useEffect(() => {
    if (!geofenceActive) {
      setFenceBlock(null);
      setSecondsLeft(null);
    }
  }, [geofenceActive]);

  // One 1s ticker drives both the poll cadence and the overlay countdown, so
  // the number on screen can't drift away from the actual next check.
  useEffect(() => {
    if (!geofenceActive) return;
    const periodMs = fenceBlocked ? FENCE_POLL_BLOCKED_MS : FENCE_POLL_MS;
    const period = periodMs / 1000;
    runFenceCheck(false, periodMs);
    let remaining = period;
    setSecondsLeft(fenceBlocked ? remaining : null);
    const id = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        runFenceCheck(false, periodMs);
        remaining = period;
      }
      // Only publish the countdown while the overlay is actually on screen — an
      // unblocked session would otherwise re-render the whole shell once a
      // second for a number nobody can see.
      if (fenceBlocked) setSecondsLeft(remaining);
    }, 1000);
    return () => clearInterval(id);
  }, [geofenceActive, fenceBlocked, runFenceCheck]);

  // Registered for EVERY authenticated user, not just ones whose cached
  // `geofenced` flag says they are fenced — that flag is exactly what is stale
  // when an admin sets a fence mid-session, which is the whole bug. Any API
  // returning GEOFENCE_LOCKED means the server already locked this session.
  useEffect(() => {
    const onLocked = () => {
      // Do NOT default to `outside`: we don't know the reason yet, and telling
      // someone they have left their area when the real problem is a blocked
      // location permission sends them to fix the wrong thing.
      setFenceBlock((b) => b || { reason: "checking" });
      refreshMe({ force: true }); // flip user.geofenced so the poll above starts
      runFenceCheck();            // resolve the real reason right now
    };
    window.addEventListener("geofence:locked", onLocked);
    return () => window.removeEventListener("geofence:locked", onLocked);
  }, [runFenceCheck, refreshMe]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdKOpen(true);
      }
      if (e.key === "Escape") setCmdKOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    try {
      // Pass this device's FCM token so the server forgets it — otherwise the
      // next account that signs in on this browser would inherit pushes
      // routed at the previous user.
      await apiLogout(getCurrentFcmToken() || undefined);
    } catch (e) {
      // even if server call fails we clear local state
    } finally {
      clearCurrentFcmToken();
      // Clear the widget's stored session so the next user starts clean.
      localStorage.removeItem("hub_widget_session_smart-ca");
      logout();
      navigate("/login", { replace: true });
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markRead(id);
      fetchNotifications();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      fetchNotifications();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="app">
      {mobileOpen && <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />}
      <Sidebar
        role={role}
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        user={user}
        badges={chatEnabled && chatUnread > 0 ? { [chatPath]: chatUnread > 99 ? "99+" : chatUnread } : {}}
      />
      <div className="main">
        <Topbar
          title={pageTitleFor(role)}
          onCmdK={() => setCmdKOpen(true)}
          onLogout={handleLogout}
          onAccount={() => navigate("/account")}
          onOpenMobileMenu={() => setMobileOpen(true)}
          unreadCount={notifications.unread_count}
          onOpenNotifications={() => setNotifOpen(true)}
          onOpenChat={chatEnabled ? () => navigate(chatPath) : undefined}
          chatUnread={chatUnread}
          attendanceEnabled={attendanceEnabled}
        />
        <SubscriptionBanner />
        <main className="content">
          <div className="content-inner">
            <Outlet />
          </div>
        </main>
      </div>

      <CmdK open={cmdKOpen} onClose={() => setCmdKOpen(false)} role={role} />
      <NotificationDrawer
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        notifications={notifications}
        onMarkRead={handleMarkRead}
        onMarkAllRead={handleMarkAllRead}
      />

      {/* Not gated on `geofenceActive` — a session whose cached flag is stale
          must still be able to show the overlay when the server locks it. */}
      {fenceBlock && (
        <GeofenceLockout
          reason={fenceBlock.reason}
          checking={fenceChecking}
          secondsLeft={secondsLeft}
          onRetry={retryFenceCheck}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}

function pageTitleFor(role) {
  if (role === "super_admin") return "Smart CA Platform";
  if (role === "admin") return "SMART CA";
  if (role === "employee") return "My Workspace";
  if (role === "customer") return "My Account";
  return "CA";
}
